
import { createNamespacedHelpers } from '@/common/vuex-composition-hooks';
import {
  defineComponent,
  onBeforeMount,
  reactive,
  computed,
  toRefs,
  watch,
  ref,
} from 'vue';
import { IAuthActions, IAuthGetters, IAuthState } from '../modules/login/types';
import BaseIcon from '@/components/BaseIcon.vue';
import CheckboxComponent from '../modules/settingPage/components/CheckboxComponent.vue';
import DropdownComponent from '../modules/settingPage/components/DropdownComponent.vue';
const Auth = createNamespacedHelpers<IAuthState, IAuthGetters, IAuthActions>(
  'auth'
);

import { useRoute } from 'vue-router';
import { firstUpperString } from '@/common/helpers/FirstUpperString';
import { PropType } from 'vue';

interface State {
  brandFilters: IFilter[];
  salesChannelFilters: IFilter[];
  billingUnitsEmp: IFilter[];
  billingUnitsFil: IFilter[];
  segmentFilters: IFilter[];
  statusFilters: IFilter[];
  carriersFilters: IFilter[];
}

interface IFilter {
  id?: number;
  index?: number;
  name?: string;
  fullname: string;
  selected?: boolean;
  code?: any;
  route?: string;
  module?: string;
}

interface FilterItem {
  data: string;
  name: string;
  param: string;
  type: string;
}

export default defineComponent({
  components: {
    BaseIcon,
    CheckboxComponent,
    DropdownComponent,
  },

  props: {
    filters: {
      type: Array as () => FilterItem[],
      required: false,
      default: () => [],
    },
    title: {
      type: String,
      default: '',
    },
    filterOptions: {
      type: Object as PropType<{
        date?: {
          enabled: boolean;
          defaultStart?: string;
          defaultEnd?: string;
          maxRangeDays?: number;
        };
        order?: {
          enabled: boolean;
          defaultValue?: string;
        };
        document?: {
          enabled: boolean;
          defaultValue?: string;
        };
      }>,
      default: () => ({
        date: { enabled: false },
        order: { enabled: false },
        document: { enabled: false },
      }),
    },
    showCurrentDate: {
      type: Boolean,
      default: true,
    },
    configFiltersValue: {
      type: Object as PropType<Record<string, IFilter[]>>,
      default: () => ({}),
    },
    showPrivateBrands: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    const otherFilters: Record<string, string> = {};
    if (this.filterOptions.date?.enabled) {
      otherFilters.dateStart = this.filterOptions.date.defaultStart || '';
      otherFilters.dateEnd = this.filterOptions.date.defaultEnd || '';
    }
    if (this.filterOptions.order?.enabled) {
      otherFilters.order = this.filterOptions.order.defaultValue || '';
    }
    if (this.filterOptions.document?.enabled) {
      otherFilters.document = this.filterOptions.document.defaultValue || '';
    }
    return {
      searchParams: {} as State,
      otherFilters,
    };
  },

  computed: {
    currentFormattedDate() {
      const date = new Date().toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      });

      return date;
    },
    disabledReason(): string {
      if (this.filterOptions.date?.enabled) {
        if (
          (this.otherFilters.dateStart && !this.otherFilters.dateEnd) ||
          (!this.otherFilters.dateStart && this.otherFilters.dateEnd)
        )
          return 'É necessário informar a data inicial e final';
      }
      if (this.filterOptions.order?.enabled && this.otherFilters.order) {
        return '';
      }
      if (this.filterOptions.date?.maxRangeDays) {
        const startDate = new Date(this.otherFilters.dateStart);
        const endDate = new Date(this.otherFilters.dateEnd);
        const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays > this.filterOptions.date.maxRangeDays) {
          return (
            'O intervalo de datas não pode ser maior que ' +
            this.filterOptions.date.maxRangeDays +
            ' dias'
          );
        }
      }
      return '';
    },
  },

  methods: {
    getFilter(filter: string) {
      let type = 'number';
      this.filters.forEach((f) => {
        if (f.data == filter) type = f.type;
      });

      if (type == 'number') {
        return this[filter].sort(
          (a, b) => Number(a.fullname) - Number(b.fullname)
        );
      } else if (type == 'string') {
        return this[filter].sort((a, b) =>
          a.fullname.localeCompare(b.fullname)
        );
      }
      return this[filter];
    },

    changeSearchParams(filter: string, option: string) {
      let i = '';
      this.searchParams[filter].forEach((f, index) => {
        if (f.id == option) i = index;
      });

      const checkboxvalue = this.searchParams[filter][i].selected;
      this.searchParams[filter][i].selected = !checkboxvalue;
      this.$emit('updateParams', this.searchParams);
    },

    setInitialSearchParams() {
      this.filters.forEach((filter) => {
        this.searchParams[filter.data] = this[filter.data];
      });
      this.$emit('updateParams', this.searchParams);
    },

    clearFilters() {
      this.filters.forEach((filter) => {
        this.searchParams[filter.data].forEach((el) => {
          el.selected = false;
        });
      });
      this.otherFilters = {};
      if (this.filterOptions.date?.enabled) {
        this.otherFilters.dateStart =
          this.filterOptions.date.defaultStart || '';
        this.otherFilters.dateEnd = this.filterOptions.date.defaultEnd || '';
      }
      this.$emit('updateParams', {
        ...this.searchParams,
        ...this.otherFilters,
      });
      this.applySearchParams();
    },

    applySearchParams() {
      if (this.disabledReason) return;
      const filters: Record<string, any> = {};
      this.filters.forEach((filter) => {
        filters[filter.param] = [];
        Object.keys(this.searchParams[filter.data]).forEach((param) => {
          if (this.searchParams[filter.data][param].selected) {
            filters[filter.param].push(
              this.searchParams[filter.data][param].code
            );
          }
        });
      });
      if (this.filterOptions.date?.enabled) {
        if (this.otherFilters.dateStart)
          filters.dateStart = this.otherFilters.dateStart;
        if (this.otherFilters.dateEnd)
          filters.dateEnd = this.otherFilters.dateEnd;
      }
      if (this.filterOptions.order?.enabled && this.otherFilters.order) {
        filters.order = this.otherFilters.order;
      }
      if (this.filterOptions.document?.enabled && this.otherFilters.document) {
        filters.document = this.otherFilters.document;
      }
      this.$emit('applySearchParams', filters);
    },
    countfilters(filter) {
      const val = this.searchParams[filter].filter(
        (param) => param.selected
      ).length;
      if (val == 0) return '';
      return ` (${val})`;
    },
    selectedFilter(value) {
      if (this.activeFilter) {
        this.otherFilters[this.activeFilter] = '';
      }
      this.activeFilter = value;
    },
  },

  setup(props) {
    const { brands } = Auth.useState(['brands']);
    const { salesChannel } = Auth.useState(['salesChannel']);
    const { billingUnits } = Auth.useState(['billingUnits']);
    const { sessionUser, getFilteredDateYYYYMM } = Auth.useGetters([
      'sessionUser',
      'getFilteredDateYYYYMM',
    ]);
    const listFilterSelected = computed(() => {
      const options = [] as any;
      if (props.filterOptions.order?.enabled) {
        options.push({ text: 'Pedido', value: 'order' });
      }
      if (props.filterOptions.document?.enabled) {
        options.push({ text: 'CPF', value: 'document' });
      }
      return options;
    });
    const activeFilter = ref('');

    const { SET_ACCESS_TYPE } = Auth.useMutations(['SET_ACCESS_TYPE']);

    const { sessionUpdate } = Auth.useActions(['sessionUpdate']);

    const route = useRoute();

    onBeforeMount(() => SET_ACCESS_TYPE('panel'));

    const state: State = reactive<State>({
      brandFilters: [],
      salesChannelFilters: [],
      billingUnitsEmp: [],
      billingUnitsFil: [],
      segmentFilters: [],
      statusFilters: [],
      carriersFilters: [],
    });

    brands?.value
      ?.filter((el) =>
        !props.showPrivateBrands ? el.brandIsPrivateLabel === 'N' : true
      )
      .forEach((el, index) => {
        state.brandFilters.push({
          id: index,
          fullname: firstUpperString(el.name),
          selected: false,
          code: el.code,
        });
      });

    salesChannel?.value?.forEach((el, index) => {
      state.salesChannelFilters.push({
        id: index,
        fullname:
          el.name == 'export' ? 'Exportação' : firstUpperString(el.name),
        selected: false,
        code: el.code,
      });
    });

    billingUnits?.value?.forEach((el) => {
      const emp = {
        id: state.billingUnitsEmp.length,
        fullname: String(el.businessCode),
        selected: false,
        code: el.businessCode,
      };

      const fil = {
        id: state.billingUnitsFil.length,
        fullname: String(el.subsidiaryCode),
        selected: false,
        code: el.subsidiaryCode,
      };

      if (!state.billingUnitsEmp.some((e) => e.code === emp.code)) {
        state.billingUnitsEmp.push(emp);
      }
      if (!state.billingUnitsFil.some((e) => e.code === fil.code)) {
        state.billingUnitsFil.push(fil);
      }
    });

    state.segmentFilters = [
      {
        id: 0,
        fullname: 'Calçado',
        selected: false,
        code: 1,
      },
      {
        id: 1,
        fullname: 'Acessório',
        selected: false,
        code: 2,
      },
      {
        id: 2,
        fullname: 'Confecção',
        selected: false,
        code: 5,
      },
    ];

    const updateStateFromProps = (config: Record<string, IFilter[]>) => {
      Object.keys(config).forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(state, key)) {
          const stateArray = state[key as keyof State] as IFilter[];
          const propArray = config[key];
          stateArray.splice(0, stateArray.length, ...propArray);
        }
      });
    };

    watch(
      () => props.configFiltersValue,
      (newConfig) => {
        updateStateFromProps(newConfig);
      },
      { deep: true, immediate: true }
    );

    return {
      ...toRefs(state),
      SET_ACCESS_TYPE,
      sessionUpdate,
      sessionUser,
      route,
      getFilteredDateYYYYMM,
      listFilterSelected,
      activeFilter,
    };
  },

  beforeMount() {
    this.setInitialSearchParams();
    this.applySearchParams();
  },
});
